// src/pages/Assignments/AssignmentForm.tsx
import React, { useEffect } from 'react';
import { CardContent, CardHeader, CardTitle } from "../../components/ui/card"
import { Button } from "../../components/ui/button"
import { Input } from "../../components/ui/input"
import { Label } from "../../components/ui/label"
import { Checkbox } from "../../components/ui/checkbox"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select"
import { useForm } from '../../hooks/useForm';
import { Assignment } from '../../types';
import { Calendar } from "../../components/ui/calendar"

interface AssignmentFormProps {
  onSubmit: (assignment: Assignment | Omit<Assignment, 'id'>) => void;
  initialAssignment: Assignment | null;
  onCancel: () => void;
}

const AssignmentForm: React.FC<AssignmentFormProps> = ({ onSubmit, initialAssignment, onCancel }) => {
  const { values, handleChange, handleCheckboxChange, handleSelectChange, resetForm, setValues } = useForm<Omit<Assignment, 'id'>>({
    date: new Date(),
    barcode: '',
    recipientName: '',
    recipientPhone: '',
    parcelWidth: '',
    parcelHeight: '',
    parcelLength: '',
    collectionCost: 0,
    needsCooling: false,
    needsHighSecurity: false,
    lockerSize: '',
    lockerType: '',
    status: 'pending',
  });

  useEffect(() => {
    if (initialAssignment) {
      setValues(initialAssignment);
    } else {
      resetForm();
    }
  }, [initialAssignment]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(initialAssignment ? { ...values, id: initialAssignment.id } : values);
    resetForm();
  };

  const handleDateChange = (date: Date | undefined) => {
    if (date) {
      setValues(prev => ({ ...prev, date }));
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <CardHeader>
        <CardTitle>{initialAssignment ? 'Edit Assignment' : 'New Assignment'}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <Label htmlFor="date">Assignment date</Label>
              <Calendar
                mode="single"
                selected={values.date}
                onSelect={handleDateChange}
                className="rounded-md border"
              />
            </div>
            <div>
              <Label htmlFor="barcode">Barcode</Label>
              <Input id="barcode" name="barcode" value={values.barcode} onChange={handleChange} />
            </div>
            <div>
              <Label htmlFor="recipientName">Recipient Name</Label>
              <Input id="recipientName" name="recipientName" value={values.recipientName} onChange={handleChange} />
            </div>
            <div>
              <Label htmlFor="recipientPhone">Recipient Phone</Label>
              <Input id="recipientPhone" name="recipientPhone" value={values.recipientPhone} onChange={handleChange} />
            </div>
            {/* Add more form fields here */}
          </div>
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <Checkbox id="needsCooling" checked={values.needsCooling} onCheckedChange={() => handleCheckboxChange('needsCooling')} />
              <Label htmlFor="needsCooling">Needs cooling</Label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox id="needsHighSecurity" checked={values.needsHighSecurity} onCheckedChange={() => handleCheckboxChange('needsHighSecurity')} />
              <Label htmlFor="needsHighSecurity">Needs high-security</Label>
            </div>
            <div>
              <Label htmlFor="lockerSize">Locker Size</Label>
              <Select onValueChange={(value) => handleSelectChange('lockerSize', value)} value={values.lockerSize}>
                <SelectTrigger id="lockerSize">
                  <SelectValue placeholder="Select size" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Small">Small</SelectItem>
                  <SelectItem value="Medium">Medium</SelectItem>
                  <SelectItem value="Large">Large</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="lockerType">Locker Type</Label>
              <Select onValueChange={(value) => handleSelectChange('lockerType', value)} value={values.lockerType}>
                <SelectTrigger id="lockerType">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Regular">Regular</SelectItem>
                  <SelectItem value="Refrigerated">Refrigerated</SelectItem>
                  <SelectItem value="High-Security">High-Security</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {/* Add more form fields here */}
            <div className="flex space-x-2">
              <Button type="submit" className="flex-1">
                {initialAssignment ? 'Update Assignment' : 'Add Assignment'}
              </Button>
              {initialAssignment && (
                <Button type="button" variant="outline" onClick={onCancel} className="flex-1">
                  Cancel
                </Button>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </form>
  );
};

export default AssignmentForm;